import React, { useState, useEffect, useContext } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Box,
    CircularProgress,
    Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import { deleteVersion, listVersions, restoreVersion } from "../API/KB/Files";
import { decideFormat } from "../App/utils";
import { AlertContext } from "../App/App";
import LocalLoader from "../Loaders/LocalLoader";

export function VersionsList({ filepathVersionsOpen, handleClose, onVersionRestored }) {
    const Alert = useContext(AlertContext);
    const [versions, setVersions] = useState([]);
    const [deletingVersions, setDeletingVersions] = useState([]);
    const [loading, setLoading] = useState(false);

    const namespace = filepathVersionsOpen?.startsWith?.('Frontend') ? 'frontend' : 'functions';

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true)
            const data = await listVersions(filepathVersionsOpen, namespace);
            setVersions(data?.data || []);
            setLoading(false)
        };

        fetchData();
    }, [filepathVersionsOpen, namespace]);

    if (!filepathVersionsOpen) return null;

    // Calculate the number of deletable versions
    const deletableVersionsCount = versions.filter(
        (version) => !deletingVersions.includes(version.VersionId)
    ).length;

    const handleDelete = async (versionId) => {
        if (deletableVersionsCount <= 1) {
            Alert.setSystemAlert({
                msg: 'Cannot delete the last remaining version.',
                type: 'error'
            });
            return;
        }

        try {
            setDeletingVersions((prev) => [...prev, versionId]);
            await deleteVersion(versionId, filepathVersionsOpen, namespace);
            const data = await listVersions(filepathVersionsOpen, namespace);
            Alert.setSystemAlert({
                msg: `Deleted version "${versionId.slice(-15)}".`,
                type: 'success'
            });
            setVersions(data?.data || []);
        } catch (error) {
            Alert.setSystemAlert({
                msg: `Failed to delete version "${versionId.slice(-15)}".`,
                type: 'error'
            });
        } finally {
            setDeletingVersions((prev) => prev.filter(id => id !== versionId));
        }
    }

    if (loading) return <LocalLoader blocking={true} open={true} />

    return (
        <div>
            <Dialog open={!!filepathVersionsOpen} onClose={handleClose} maxWidth='100%' style={{ zIndex: 10000 }}>
                <DialogTitle>History of {filepathVersionsOpen}</DialogTitle>
                <DialogContent>
                    <Box style={{ overflow: 'auto' }}>
                        <List>
                            {versions.map((version) => {
                                const isDeleting = deletingVersions.includes(version.VersionId);
                                const isDeleteDisabled = deletableVersionsCount <= 1;

                                return (
                                    <ListItem key={version.VersionId} style={{ paddingRight: '70px' }}>
                                        <ListItemText
                                            primary={`${version.IsLatest ? `current` : decideFormat(version.LastModified)} (${version.VersionId.slice(-15)})`}
                                            style={{ marginRight: 50 }}
                                        />
                                        <ListItemSecondaryAction style={{zIndex: 2000}}>
                                            <Tooltip title={isDeleteDisabled ? "Cannot delete the last remaining version." : "Delete version"}>
                                                <span >
                                                    <IconButton
                                                        edge="end"
                                                        aria-label="delete"
                                                        style={{ marginRight: '2px' }}
                                                        onClick={() => handleDelete(version.VersionId)}
                                                        disabled={isDeleteDisabled || isDeleting}
                                                    >
                                                        {isDeleting ? <CircularProgress size={24} /> : <DeleteIcon fontSize="small" />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <IconButton
                                                edge="end"
                                                aria-label="restore"
                                                onClick={async () => {
                                                    if (versions.length === 1) {
                                                        Alert.setSystemAlert({ msg: 'This is already the current version', type: 'error' });
                                                        return;
                                                    }

                                                    try {
                                                        const restoredVersionContent = await restoreVersion(version.VersionId, filepathVersionsOpen, namespace);
                                                        onVersionRestored(filepathVersionsOpen, version.VersionId, restoredVersionContent?.data, namespace);
                                                        Alert.setSystemAlert({
                                                            msg: `File "${filepathVersionsOpen}" has been restored to ${version.IsLatest ? `current version` : ('version from ' + decideFormat(version.LastModified))}`,
                                                            type: 'success'
                                                        });
                                                    } catch (error) {
                                                        Alert.setSystemAlert({
                                                            msg: `Failed to restore version "${version.VersionId.slice(-15)}".`,
                                                            type: 'error'
                                                        });
                                                    }
                                                }}
                                            >
                                                <RestoreIcon fontSize="small" />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary" variant={"outlined"}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}